import * as Sentry from '@sentry/browser';

import * as APIClient from 'Services/APIClient';
import * as UserService from 'Services/UserService';

import { productsSlugs } from 'constants/products';
import { subscriptionCategories } from 'constants/subscriptions';

import { trackCreateAddress, trackFetchUser, trackUpdateAddress } from 'dux/tracking/actions';

import {
  ADDRESSES_FAILURE,
  ADDRESSES_REQUEST,
  ADDRESSES_SUCCESS,
  CLEAR_REFERRAL,
  CREATE_ADDRESS_SUCCESS,
  CREATE_PAYMENT_METHOD_SUCCESS,
  PATCH_USER_FAILURE,
  PATCH_USER_REQUEST,
  PATCH_USER_SUCCESS,
  PAYMENT_METHODS_FAILURE,
  PAYMENT_METHODS_REQUEST,
  PAYMENT_METHODS_SUCCESS,
  RECOMMENDATIONS_FAILURE,
  RECOMMENDATIONS_REQUEST,
  RECOMMENDATIONS_SUCCESS,
  SEEN_PROMOTIONAL_MODAL,
  SET_CUSTOMER_ORIGIN,
  SET_LIVECHAT_GENERATION,
  SET_LIVECHAT_OPENING,
  SET_REFERRAL,
  SET_TOGGLE_PROMOTIONAL_TOAST,
  SET_USER_COUNTRY,
  SUPPLEMENTS_VARIANT_FETCH_ERROR,
  SUPPLEMENTS_VARIANT_FETCH_REQUEST,
  SUPPLEMENTS_VARIANT_FETCH_SUCCESS,
  USER_FAILURE,
  USER_REQUEST,
  USER_SUCCESS,
} from './actionTypes';
import {
  getPromotionToastContent,
  getPromotionToastStyle,
  getShouldDisplayToast,
} from './selectors';

// ----------------------------------------------------------------------------

export const setCustomerOrigin = customer_origin => ({
  type: SET_CUSTOMER_ORIGIN,
  customer_origin,
});

// ----------------------------------------------------------------------------

export const clearReferral = () => ({
  type: CLEAR_REFERRAL,
});

export const setReferral = referral => {
  return {
    type: SET_REFERRAL,
    referral: {
      code: referral?.code,
      source: referral?.source,
    },
  };
};

export const openPromotionToast = ({ content, style } = {}) => ({
  type: SET_TOGGLE_PROMOTIONAL_TOAST,
  shouldDisplayToast: true,
  content,
  style,
});

export const closePromotionToast = () => ({
  type: SET_TOGGLE_PROMOTIONAL_TOAST,
  shouldDisplayToast: false,
});

export const setRootToast =
  ({ content, style }) =>
  async (dispatch, getState) => {
    const state = getState();
    const isAlreadyOpen = getShouldDisplayToast(state);
    const previousContent = getPromotionToastContent(state);
    const previousStyle = getPromotionToastStyle(state);

    const sameAsPreviousToast =
      content === previousContent &&
      style?.color === previousStyle?.color &&
      style?.backgroundColor === previousStyle?.backgroundColor;

    if (!isAlreadyOpen || (isAlreadyOpen && sameAsPreviousToast)) {
      dispatch(openPromotionToast({ content, style }));
    }

    if (isAlreadyOpen && !sameAsPreviousToast) {
      dispatch(closePromotionToast());
      setTimeout(() => {
        dispatch(openPromotionToast({ content, style }));
      }, 250); // ANIMATE_OUT_DURATION in BasicToast component
    }
  };
// ----------------------------------------------------------------------------

export const requestUser = () => ({ type: USER_REQUEST });
export const receiveUser = user => ({
  type: USER_SUCCESS,
  user,
});
export const receiveUserError = error => ({ type: USER_FAILURE, error });

export const fetchUser = () => async dispatch => {
  dispatch(requestUser());
  try {
    const user = await UserService.fetch();
    dispatch(receiveUser(user));
    dispatch(trackFetchUser(user));
  } catch (err) {
    dispatch(receiveUserError(err));
    throw err;
  }
};

// ----------------------------------------------------------------------------

export const patchUser = data => async dispatch => {
  dispatch({ type: PATCH_USER_REQUEST });
  try {
    const user = await UserService.patch(data);
    dispatch({
      type: PATCH_USER_SUCCESS,
      user,
    });
    dispatch(trackFetchUser(user));
  } catch (error) {
    dispatch({ type: PATCH_USER_FAILURE, error });
    throw error;
  }
};

// ----------------------------------------------------------------------------

const requestAddresses = () => ({ type: ADDRESSES_REQUEST });
const receiveAddresses = addresses => ({ type: ADDRESSES_SUCCESS, addresses });
const receiveAddressesError = error => ({ type: ADDRESSES_FAILURE, error });

export const fetchAddresses = () => async dispatch => {
  dispatch(requestAddresses());
  try {
    const result = await UserService.fetchAddresses();
    dispatch(receiveAddresses(result));
    dispatch(trackUpdateAddress(result));
  } catch (err) {
    dispatch(receiveAddressesError(err));
    Sentry.captureMessage('API action (User.fetchAddresses)');
  }
};

const createAddressSuccess = address => ({ type: CREATE_ADDRESS_SUCCESS, address });

export const createAddress = address => async dispatch => {
  try {
    const result = await UserService.createAddress(address);
    dispatch(trackCreateAddress(result));
    return dispatch(createAddressSuccess(result));
  } catch (err) {
    Sentry.captureMessage('API action (User.createAddress)');
    throw err;
  }
};

// ----------------------------------------------------------------------------

const requestPaymentMethods = () => ({ type: PAYMENT_METHODS_REQUEST });
const receivePayment = paymentMethods => ({ type: PAYMENT_METHODS_SUCCESS, paymentMethods });
const receivePaymentError = error => ({ type: PAYMENT_METHODS_FAILURE, error });

export const fetchPaymentMethods = () => async dispatch => {
  dispatch(requestPaymentMethods());
  try {
    const result = await UserService.fetchPaymentMethods();
    dispatch(receivePayment(result));
  } catch (err) {
    dispatch(receivePaymentError(err));
    Sentry.captureMessage('API action (User.fetchPaymentMethods)');
  }
};

const createPaymentMethodSuccess = paymentMethod => ({
  type: CREATE_PAYMENT_METHOD_SUCCESS,
  paymentMethod,
});

export const createPaymentMethod = paymentMethod => async dispatch => {
  try {
    const result = await UserService.createPaymentMethod(paymentMethod);
    dispatch(createPaymentMethodSuccess(result));
    return result;
  } catch (err) {
    Sentry.captureMessage('API action (User.createPaymentMethod)');
    throw err;
  }
};

export const requestRecommendations = () => ({ type: RECOMMENDATIONS_REQUEST });
export const receiveRecommendations = recommendations => ({
  type: RECOMMENDATIONS_SUCCESS,
  recommendations,
});
export const receiveRecommendationsError = error => ({ type: RECOMMENDATIONS_FAILURE, error });

export const fetchRecommendations = subscriptionCategory => async dispatch => {
  const category =
    subscriptionCategory === subscriptionCategories.SKINCARE
      ? subscriptionCategories.SKINCARE
      : subscriptionCategories.HAIRCARE;

  dispatch(requestRecommendations());

  try {
    const recommendations = await UserService.fetchRecommendations(category);
    dispatch(receiveRecommendations(recommendations));
  } catch (err) {
    dispatch(receiveRecommendationsError(err));
    throw err;
  }
};

// ----------------------------------------------------------------------------

export const fetchSupplementsVariant = () => async dispatch => {
  dispatch({ type: SUPPLEMENTS_VARIANT_FETCH_REQUEST });

  try {
    const response = await APIClient.postJson('/v1/account/hair_profile/formula', {
      skeleton: productsSlugs.SUPPLEMENT_CORE,
    });
    dispatch({ type: SUPPLEMENTS_VARIANT_FETCH_SUCCESS, response });
  } catch (error) {
    Sentry.withScope(scope => {
      scope.setLevel('error');
      Sentry.captureMessage(`[Account Supplements] ${error.toString()}`);
    });
    dispatch({ type: SUPPLEMENTS_VARIANT_FETCH_ERROR, error });
  }
};

// ----------------------------------------------------------------------------

export const setLivechatGeneration = () => async dispatch => {
  dispatch({ type: SET_LIVECHAT_GENERATION, hasGeneratedLivechat: true });
};

export const setLivechatOpening = () => async dispatch => {
  dispatch({ type: SET_LIVECHAT_OPENING, hasOpenedLivechat: true });
};

export const setUserCountry = userCountry => async dispatch => {
  dispatch({ type: SET_USER_COUNTRY, geolocationGuessedCountry: userCountry });
};

// ----------------------------------------------------------------------------

export const seenPromotionalModal = seenModalName => async dispatch => {
  dispatch({ type: SEEN_PROMOTIONAL_MODAL, payload: seenModalName });
};
